import React, { useCallback, useEffect, useState } from 'react';
import { ulid } from 'ulid';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Storage } from 'aws-amplify';
import AddEvent from './AddEvent/AddEvent';
import AddRecurringEvents from './AddRecurringEvents/AddRecurringEvents';
import CreateRetailSpace from './CreateRetailSpace';
import CreateRetailProfile from './CreateRetailProfile';
import ManageRetailSpaces from './ManageRetailSpaces';
import DashboardTabs from './components/Tabs/Tabs';
import { CustomSelect, Loading, Layout, UserNavbar } from '../../../../components';
import ManageEvents from './ManageEvents/ManageEvents';
import * as EventActions from '../../../../core/events/action';
import * as VenueActions from '../../../../core/venues/action';
import { onDisplayAlert } from '../../../../utils/alerts/alerts';
import PastEvents from '../components/PastEvents';
import Banner from '../../venues/components/Banner/Banner';
import { APP_ROUTES } from '../../../../constants/app-routes';
import '../styles.scss';
import { useIsMobile } from '../../../../hooks/useIsMobile';

const sortOptions = [
  { id: 'all', name: 'All Events' },
  { id: 'current', name: 'Current Events' },
  { id: 'past', name: 'Past Events' },
];

export default function VenuesDashboard() {
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [title, setTitle] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [loadingBanner, setLoadingBanner] = useState(false);

  const { currentVenue, currentVenuePictures, principalBanner } = useSelector(
    (store) => store.venues,
  );
  const { tabsEvent, currentTab, loading } = useSelector((store) => store.events);
  const { user, loading: userLoading } = useSelector((store) => store.users);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const onLoadBannerFromAllVenuePhotos = useCallback(async () => {
    const [FirstPicture] = currentVenuePictures;
    setLoadingBanner(true);
    const signedURL = await Storage.get(FirstPicture.key);
    dispatch(VenueActions.setPrincipalBannerVenue(signedURL));
    setLoadingBanner(false);
  }, [currentVenuePictures, dispatch]);

  const onLoadBannerFromVenueBanner = useCallback(
    async (photo) => {
      setLoadingBanner(true);
      const signedURL = await Storage.get(photo);
      dispatch(VenueActions.setPrincipalBannerVenue(signedURL));
      setLoadingBanner(false);
    },
    [dispatch],
  );

  const uploadPictures = useCallback(
    async (pics) => {
      const fileUploaded = [];
      try {
        setLoadingUpload(true);
        await Promise.all(
          pics.map(async ({ file }) => {
            const fileName = `${ulid()}.${file.name.split('.')[1]}`;
            const ready = await Storage.put(`VENUES/${user.id}/${fileName}`, file, {
              contentType: file.type,
            });
            fileUploaded.push({ ...ready, fileName });
          }),
        );
      } catch (error) {
        console.error('Error uploading file: ', error);
      }
      return fileUploaded;
    },
    [user?.id],
  );

  useEffect(() => {
    if (currentVenue?.banner) onLoadBannerFromVenueBanner(currentVenue.banner);
    else if (currentVenuePictures.length > 0)
      onLoadBannerFromAllVenuePhotos(currentVenuePictures[0]);
  }, [
    currentVenue?.banner,
    currentVenuePictures,
    onLoadBannerFromAllVenuePhotos,
    onLoadBannerFromVenueBanner,
  ]);

  useEffect(() => {
    if (currentVenue) setTitle(currentVenue.name);
  }, [currentVenue]);

  const onChangeTab = (id) => {
    if (!currentVenue) {
      onDisplayAlert({
        title: 'Venue Information',
        description: 'Before continuing you must enter the details of your venue',
        type: 'info',
      });
    } else {
      dispatch(EventActions.onChangeTabEvent(id));
    }
  };

  const onSaveEvent = async (event) => {
    const fileUploaded = await uploadPictures(event.event.photoEvent);
    const updatedEvent = { ...event };
    updatedEvent.event.photo = fileUploaded[0].key;
    dispatch(EventActions.createEvent(event, navigate));
    // dispatch(
    //   VenueActions.updateVenue({
    //     ...event.event.venue,
    //     fk_user: user.id,
    //     pictures: fileUploaded,
    //   }),
    // );
  };

  const onSaveRecurringEvents = async (events) => {
    // Upload pictures once and reuse for all events
    const fileUploaded = await uploadPictures(events[0].event.photoEvent);
    const photoKey = fileUploaded[0].key;

    // Create all events sequentially using reduce to avoid for-await loop
    await events.reduce(async (previousPromise, event) => {
      await previousPromise;
      const updatedEvent = { ...event };
      updatedEvent.event.photo = photoKey;
      dispatch(EventActions.createEvent(updatedEvent, navigate));
      // Wait a bit before the next one to avoid overwhelming the server
      return new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
    }, Promise.resolve());
  };

  const onHandlerSortBy = (e) => {
    dispatch(EventActions.onSortEvents(e.target.value));
    setSortBy(e.target.value);
  };

  const getCurrentPage = () => {
    const pages = {
      1: <ManageEvents />,
      2: <AddEvent onSaveEvent={onSaveEvent} />,
      5: <AddRecurringEvents onSaveEvents={onSaveRecurringEvents} />,
      3: '',
      4: <PastEvents />,
      6: <ManageRetailSpaces />,
      // 7: <CreateRetailSpace />, // Hidden for now - feature to be included later
      8: <CreateRetailProfile />,
    };
    return pages[currentTab];
  };

  return (
    <Layout>
      {(loading || loadingBanner || userLoading) && <Loading />}
      <UserNavbar />
      <Banner picture={principalBanner} />

      {user && !user?.stripe_details && (
        <Box display="flex" justifyContent="center">
          <Box width={isMobile ? '90%' : '70%'}>
            <Alert
              variant="filled"
              action={
                <Box display="flex" alignContent="center" height="100%">
                  <Button
                    sx={{ color: 'white' }}
                    onClick={() => navigate(APP_ROUTES.VENDOR_PROFILE)}>
                    Set it now
                  </Button>
                </Box>
              }
              severity="warning">
              <AlertTitle>WARNING</AlertTitle>
              You haven&apos;t set-up your Stripe account.
              <br />
              Receiving payments from fayVen through alternate payout methods other than Stripe may
              result in additional processing fees.
            </Alert>
          </Box>
        </Box>
      )}
      <DashboardTabs
        title={title}
        tabActive={currentTab}
        tabs={tabsEvent}
        onChangeTab={onChangeTab}
      />

      {currentTab === 1 && (
        <Grid container justifyContent="start" paddingLeft={20}>
          <Grid item sm={1} marginTop={4} marginLeft={10}>
            <Typography variant="body" color="#EE7B57" fontWeight="bold">
              Sort by:
            </Typography>
          </Grid>
          <Grid item sm={2}>
            <CustomSelect value={sortBy} options={sortOptions} handleChange={onHandlerSortBy} />
          </Grid>
        </Grid>
      )}
      <Grid container justifyContent="space-evenly">
        <Typography variant="h3" color="#EE7B57" fontWeight="bold" marginTop={4} align="center">
          {title}
        </Typography>
      </Grid>
      {getCurrentPage()}
    </Layout>
  );
}
